import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useTheme } from 'contexts/ThemeContext';
import { X } from 'lucide-react';

interface OTPModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (otp: string) => void;
  title: string;
  message: string;
  confirmText?: string;
  confirmColor?: 'primary' | 'danger';
}

const OTPModal: React.FC<OTPModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Verify',
  confirmColor = 'primary',
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [otp, setOtp] = useState<string>('');

  if (!isOpen) return null;

  // Color tokens
  const modalBg = isDark ? '#1E293B' : '#FFFFFF';
  const headerBorder = isDark ? '#314158' : '#E5E7EB';
  const titleColor = isDark ? '#E2E8F0' : '#111827';
  const messageColor = isDark ? '#90A1B9' : '#4B5563';
  const inputBg = isDark ? '#0F172B' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#D1D5DB';
  const inputText = isDark ? '#E2E8F0' : '#111827';
  const inputFocusBorder = '#2B7FFF';
  const cancelBg = isDark ? '#1E293B' : '#FFFFFF';
  const cancelBorder = isDark ? '#314158' : '#D1D5DB';
  const cancelText = isDark ? '#E2E8F0' : '#374151';
  const closeColor = isDark ? '#90A1B9' : '#9CA3AF';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.trim()) {
      onConfirm(otp);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
      <div className="rounded-xl w-80 max-w-[90%] shadow-2xl" style={{ background: modalBg }}>
        {/* Header */}
        <div
          className="flex justify-between items-center p-3"
          style={{ borderBottom: `1px solid ${headerBorder}` }}
        >
          <h2 style={{ fontSize: '14px', fontWeight: 600, color: titleColor, margin: 0 }}>{title}</h2>
          <button
            onClick={onClose}
            className="transition-colors hover:opacity-80"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: closeColor, display: 'flex', alignItems: 'center' }}
          >
            <X size={16} />
          </button>
        </div>
        {/* Body */}
        <div className="p-3">
          <p style={{ fontSize: '12px', color: messageColor, marginBottom: '8px' }}>{message}</p>
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder={t('modals.otp.placeholder')}
              className="w-full"
              style={{
                padding: '8px 10px',
                background: inputBg,
                border: `1px solid ${inputBorder}`,
                borderRadius: '8px',
                fontSize: '12px',
                color: inputText,
                outline: 'none',
                marginBottom: '12px',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => { e.target.style.borderColor = inputFocusBorder; }}
              onBlur={(e) => { e.target.style.borderColor = inputBorder; }}
              maxLength={6}
              autoFocus
            />
            <div className="flex justify-end gap-1.5">
              <button
                type="submit"
                style={{
                  padding: '6px 14px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#FFFFFF',
                  border: 'none',
                  cursor: 'pointer',
                  background: confirmColor === 'primary'
                    ? 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)'
                    : '#EF4444',
                }}
              >
                {confirmText}
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '6px 14px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: cancelBg,
                  color: cancelText,
                  border: `1px solid ${cancelBorder}`,
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default OTPModal;
